import { useState, useEffect, useCallback } from 'react';

/**
 * Shared include/exclude logic for task types (RotaTypes) per team group,
 * used by Settings/RotaTypeGroupSettings and TeamGroupTasksOverlay. Every
 * task type is included for every group by default; a group only loses one
 * via an explicit exclusion. A successful toggle shows no message - the
 * button's new state confirms it - so only errors are passed to setNotice.
 * @param {object} api
 * @param {function} setNotice ({ text, isError } | null) => void
 */
const useRotaTypeToggle = (api, setNotice) => {

    const [rotaTypes, setRotaTypes] = useState([]);
    const [savingRotaTypeID, setSavingRotaTypeID] = useState(null);

    const loadRotaTypes = useCallback(() => {
        api.transact('TeamGroupRotaTypeMatrix', {})
            .then(r => r.apiResult)
            .then(r => setRotaTypes(r.RotaTypes || []))
            .catch(e => setNotice({ text: e.apiText || e.message || 'Failed to load task types', isError: true }));
    }, [api, setNotice]);

    useEffect(() => { loadRotaTypes(); }, [loadRotaTypes]);

    const isIncluded = (rotaType, groupID) => !rotaType.ExcludedGroupIDs?.includes(groupID);

    const toggleTask = (rotaType, groupID) => {

        const included = isIncluded(rotaType, groupID);

        const request = included
            ? api.transact('TeamGroupRotaTypeExclusionUpdate', { GroupID: groupID, RotaTypeID: rotaType.RotaTypeID })
            : api.transact('TeamGroupRotaTypeExclusionDelete', { GroupID: groupID, RotaTypeID: rotaType.RotaTypeID });

        setNotice(null);
        setSavingRotaTypeID(rotaType.RotaTypeID);

        request
            .then(r => setRotaTypes(r.apiResult.RotaTypes || []))
            .catch(e => setNotice({ text: e.apiText || e.message || 'Failed to update task type', isError: true }))
            .finally(() => setSavingRotaTypeID(null));
    };

    return { rotaTypes, setRotaTypes, savingRotaTypeID, isIncluded, toggleTask };
};

export default useRotaTypeToggle;
